/**
 * Frontend client for the backend transaction relay.
 *
 * The relay (`POST /api/relay/transaction` — `workers/src/services/relay/handler.ts`)
 * is the only path frontend code uses to *send* a signed transaction.
 * Direct `rpc.sendTransaction` calls are reserved for the backend; the
 * frontend's RPC use is read-only.
 *
 * The relay authenticates from the transaction signature itself
 * (`workers/src/middleware/middleware.ts#authMiddleware`), so this client
 * does not need to attach a separate HMAC. It still travels through
 * `apiClient` so the JWT goes along — handy for rate-limit keying — but
 * the relay does not require it.
 *
 * Encoding: the relay parses the transaction with its own bs58 decoder
 * and rejects anything else (`workers/src/services/relay/validator.ts`).
 * We use `bs58` (already a workspace dep) to encode the wire bytes.
 */

import bs58 from "bs58";
import { getBase64EncodedWireTransaction } from "@solana/kit";
import type { Transaction } from "@solana/kit";

import { apiClient } from "./client";

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export type RelayCommitment = "processed" | "confirmed" | "finalized";

export interface RelayTransactionResult {
  signature: string;
  /** Which RPC successfully accepted the broadcast (informational). */
  rpc: "primary" | "secondary" | "tertiary";
  confirmed: boolean;
  confirmationStatus?: RelayCommitment;
  slot?: number;
}

interface RelayEnvelope {
  success: boolean;
  data: RelayTransactionResult;
}

export interface SendSignedTransactionParams {
  /** Optional Cloudflare Turnstile token (required when TURNSTILE_ENFORCEMENT=on). */
  turnstileToken?: string;
  /**
   * Whether to wait for confirmation server-side before resolving.
   * Defaults to `true` to mirror the previous `rpc.sendTransaction +
   * registerAndPoll` UX in a single round-trip.
   */
  waitForConfirmation?: boolean;
  commitment?: RelayCommitment;
}

/**
 * Encode + relay a signed Kit transaction.
 *
 * Throws on validation, rate-limit, or RPC fanout failures — callers
 * should keep their existing wallet-rejection / insufficient-funds
 * detection on the message string.
 */
export async function sendSignedTransaction(
  signed: Transaction,
  params: SendSignedTransactionParams = {},
): Promise<RelayTransactionResult> {
  // Kit only re-exports the base64 wire encoder; the relay validator
  // expects base58, so rebase via raw bytes.
  const wireBytes = base64ToBytes(getBase64EncodedWireTransaction(signed));
  const transaction = bs58.encode(wireBytes);

  const envelope = await apiClient.post<RelayEnvelope>(
    "/api/relay/transaction",
    {
      transaction,
      waitForConfirmation: params.waitForConfirmation ?? true,
      commitment: params.commitment ?? "confirmed",
      turnstileToken: params.turnstileToken,
    },
  );

  return envelope.data;
}
